
import { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { OpenApiRequestStatus, useOpenApiRequest } from 'typescript-request-utils';
import { useDispatch, useSelector } from 'react-redux';

import Menu from '../view/Menu';
import Header from '../view/Header';
import { EntityQueryResults } from '../../../api/models/EntityQueryResults';
import { Organization } from '../../../api/models/Organization';
import { getApiRegistry } from '../../state/apiRegistry';
import { getOrganizationsInitialized, getWindowWidth, setAccessToken,
    setPendingReceivedApplicationsCount, setCurrentUser, setIsOrganizationUser,
    setOrganizationsInitialized, setWindowWidth, setEndingRepresentations,
    setEndingAuthorizedRepresentations, 
    getCurrentUser} from '../../../features/basic/reducers/basicReducer';
import AppRouter from '../../../routes/AppRouter';
import { ViewType } from '../../../page/enums/ViewType';
import { Application } from '../../../api/models/Application';
import { useTheme, useVariables } from '../../../features/context/CustomerContext';
import { requestHasEndingRepresenattions } from '../../../utilities/RepresentationUtil';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { UserRepresentation } from '../../../api/models/UserRepresentation';
import { UserInfo } from '../../../api/models/UserInfo';
import i18n from '../../../i18n/config';

export const BaseApp = () => {
    const auth = useAuth();
    const dispatch = useDispatch();
    const theme = useTheme();
    const variables = useVariables();
    
    const[organizationsRequest, loadOrganizations] = useOpenApiRequest(getApiRegistry().organizationsApi.getOrganizations);
    const[pendingReceivedApplicationsCountRequest, loadPendingReceivedApplicationsCount] = useOpenApiRequest(getApiRegistry().applicationsApi.getRepresentationApplications);
    const[customerAuthorizationsRequest, loadCustomerAuthorizations] = useOpenApiRequest(getApiRegistry().userCustomerApi.getUserCustomers);
    const[authorizedUsersRequest, loadAuthorizedUsers] = useOpenApiRequest(getApiRegistry().userCustomerApi.getAuthorizedUsers);
    const[userProfileRequest, getUserProfile] = useOpenApiRequest(getApiRegistry().usersApi.getUserProfile);
    const[userInfoRequest, getUserInfo] = useOpenApiRequest(getApiRegistry().usersApi.getCurrentUserInfo);

    const[hasTriedSigning, setHasTriedSignin] = useState(false);

    const windowWidth = useSelector(getWindowWidth);
    const organizationsInitialized = useSelector(getOrganizationsInitialized);
    const currentUser = useSelector(getCurrentUser);

    useEffect(() => {
        // First login
        if(!hasTriedSigning && !currentUser) {
            if(!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading) {
                setHasTriedSignin(true);
                auth.signinRedirect();
            }
            // If user presses back button on browser and is not authenticated but the url has auth params
            // reload page without query params
            else if(hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading) {
                window.location.href = window.location.pathname;
            }
            else if(auth.isAuthenticated) {
                dispatch(setAccessToken(auth.user?.access_token));
                getUserProfile();
                getUserInfo();
                loadOrganizations({});
                loadPendingReceivedApplicationsCount({type: ViewType.RECEIVED_APPLICATIONS});
                if (auth.user?.id_token) {
                    window.sessionStorage.setItem("keycloakIdToken", auth.user.id_token);
                    window.dispatchEvent(new Event("keycloakIdToken"));
                }
                loadAuthorizedUsers({filter: "", sort: "", filterParam: "", pageNumber: 1, pageSize: 20});
            }
        }
    }, [auth, hasTriedSigning, currentUser, dispatch, getUserInfo, getUserProfile, loadAuthorizedUsers, loadOrganizations, loadPendingReceivedApplicationsCount]);


    // Renew access token silently in the background
    useEffect(() => {
        const renewAccessToken = async () => {
            try {
                console.log("Starting to renew access token");
                await auth.signinSilent();
            } catch(err) {
                console.error('An error occured while renewing access token' + err);
            }
        }
        const userLoaded = async (user: any) => {
            if(user.access_token) {
                console.log('Renewed access token successfully');
                dispatch(setAccessToken(user.access_token));
            }
            else {
                console.error('Failed to renew access token');
            }
        }
        auth.events.addAccessTokenExpiring(renewAccessToken);
        auth.events.addUserLoaded(userLoaded);
        return () => {
            auth.events.removeAccessTokenExpiring(renewAccessToken);
            auth.events.removeUserLoaded(userLoaded);
        };
    }, [auth, dispatch]);


    useEffect(() => {
        if (userProfileRequest.type === OpenApiRequestStatus.Complete) {
            const userProfile: UserRepresentation = userProfileRequest.responsePayload;
            dispatch(setCurrentUser(userProfile));
            loadCustomerAuthorizations({pageNumber: 1, pageSize: 20, sort: "", filter: "", filterParam: "", usernames: userProfile.username});
        }
    }, [userProfileRequest, dispatch, loadCustomerAuthorizations]);
    
    useEffect(() => {
        if(userInfoRequest.type === OpenApiRequestStatus.Complete) {
            const userInfo: UserInfo = userInfoRequest.responsePayload;
            for(let language of variables.localizationLanguages) {
                if(userInfo && language.languageAbbreviation === userInfo.locale){
                    i18n.changeLanguage(language.languageAbbreviation);
                }
            }
        }
    }, [userInfoRequest, variables.localizationLanguages]);

    useEffect(() => {
        const handleResizeWindow = () => dispatch(setWindowWidth(window.innerWidth));
         // subscribe to window resize event "onComponentDidMount"
        window.addEventListener("resize", handleResizeWindow);
        return () => {
            // unsubscribe "onComponentDestroy"
            window.removeEventListener("resize", handleResizeWindow);
        };
        // eslint-disable-next-line 
    }, []);

    useEffect(() => {
        if (organizationsRequest.type === OpenApiRequestStatus.Complete) {
            const organizations: EntityQueryResults<Organization> = organizationsRequest.responsePayload;
            dispatch(setIsOrganizationUser(organizations && organizations.totalEntities > 0));
            dispatch(setOrganizationsInitialized(true));
        } else if (organizationsRequest.type === OpenApiRequestStatus.NetworkError || organizationsRequest.type === OpenApiRequestStatus.ServerError){
            dispatch(setOrganizationsInitialized(true));
        }
      }, [dispatch, organizationsRequest]);

    useEffect(() => {
        if (pendingReceivedApplicationsCountRequest.type === OpenApiRequestStatus.Complete) {
            const pendingReceivedApplications: EntityQueryResults<Application> = pendingReceivedApplicationsCountRequest.responsePayload;
            dispatch(setPendingReceivedApplicationsCount(pendingReceivedApplications?.totalEntities));
        }
        // eslint-disable-next-line 
      }, [pendingReceivedApplicationsCountRequest]);

      useEffect(() => {
        if (customerAuthorizationsRequest.type === OpenApiRequestStatus.Complete) {
            dispatch(setEndingRepresentations(requestHasEndingRepresenattions(customerAuthorizationsRequest.responsePayload)))
        }
        // eslint-disable-next-line
    }, [customerAuthorizationsRequest]);

    useEffect(() => {
        if (authorizedUsersRequest.type === OpenApiRequestStatus.Complete) {
            dispatch(setEndingAuthorizedRepresentations(requestHasEndingRepresenattions(authorizedUsersRequest.responsePayload)));
        }
    // eslint-disable-next-line
    }, [authorizedUsersRequest]);

    return (
        <BrowserRouter basename={"/asiakashallinta"}>
            <Header/>
            {organizationsInitialized && windowWidth >= theme.mobileMenuBreakpoint && <Menu />}
            <AppRouter />
        </BrowserRouter>
    );
};